import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import DeviceDropdown from '../components/dashboard/DeviceDropdown';
import IntervalButtons from '../components/dashboard/IntervalButtons';
import LoadingMessage from '../components/common/LoadingMessage';
import ErrorMessage from '../components/common/ErrorMessage';
import { useDiscoveryData } from '../hooks/useDiscoveryData';
import './NetworkDiagram.css';

/**
 * AGGRESSIVE RESIZEOBSERVER ERROR SUPPRESSION
 * These errors are harmless notifications about layout loops that often happen with vis-network.
 * In development, they can trigger the CRA error overlay, so we suppress them here.
 */
if (typeof window !== 'undefined') {
    const originalError = window.onerror;
    window.onerror = function (message, source, lineno, colno, error) {
        if (message && (message.includes('ResizeObserver loop completed') ||
            message.includes('ResizeObserver loop limit exceeded'))) {
            // Check for CRA/Webpack overlay and hide it if it's there
            const overlays = [
                document.getElementById('webpack-dev-server-client-overlay'),
                document.getElementById('webpack-dev-server-client-overlay-div')
            ];
            overlays.forEach(overlay => {
                if (overlay) overlay.style.display = 'none';
            });
            return true; // Stop the error from propagating
        }
        if (originalError) return originalError.apply(this, arguments);
    };

    window.addEventListener('error', (e) => {
        if (e.message && (e.message.includes('ResizeObserver loop completed') ||
            e.message.includes('ResizeObserver loop limit exceeded'))) {
            e.stopImmediatePropagation();
            e.stopPropagation();
            // Hide CRA overlay
            const overlays = [
                document.getElementById('webpack-dev-server-client-overlay'),
                document.getElementById('webpack-dev-server-client-overlay-div')
            ];
            overlays.forEach(overlay => {
                if (overlay) overlay.style.display = 'none';
            });
        }
    }, true);
}

const getIconGroup = (device) => {
    const vendor = (device.vendor || "").toLowerCase();
    const hostname = (device.hostname || "").toLowerCase();
    const snmp = (device.snmp || "").toLowerCase();
    const devType = device.type || 'LAN';
    const isAp = device.is_ap || false;

    const textSearch = `${vendor} ${hostname} ${snmp}`;

    if (devType === 'GATEWAY') return 'gateway';
    if (isAp) return 'ap';

    if (textSearch.includes("epson") || textSearch.includes("hp")) return 'printer';
    if (textSearch.includes("hikvision") || hostname.includes("cam") || vendor.includes("vision")) return 'camera';
    if (textSearch.includes("raspberry pi") || hostname.includes("pi") || textSearch.includes("rpi")) return 'rpi';
    if (textSearch.includes("apple") || textSearch.includes("iphone") || textSearch.includes("samsung") ||
        textSearch.includes("ipad") || textSearch.includes("galaxy") || textSearch.includes("s25")) return 'mobile';
    if (vendor.includes("espressif")) return 'chip';

    return 'desktop';
};

const NetworkMap = ({ data, height = "500px", isModal = false }) => {
    const containerRef = useRef(null);
    const networkRef = useRef(null);

    useEffect(() => {
        if (!data || !containerRef.current || !window.vis) return;

        let network = null;

        /** 
         * DELAYED INITIALIZATION
         * Modals have CSS transitions (0.3s). vis-network triggers resize events while the modal
         * is growing, causing ResizeObserver errors. We wait 450ms for the modal to be stable.
         */
        const waitTime = isModal ? 450 : 150;

        const timer = setTimeout(() => {
            if (!containerRef.current) return;

            const nodes = [];
            const edges = [];

            // Center Node (The scanner ESP32)
            nodes.push({
                id: 'ESP32',
                label: `ESP32 Scanner\n${data.scanner_mac}`,
                group: 'esp32',
                title: `Scanner MAC: ${data.scanner_mac}\nTime: ${new Date(data.scan_timestamp).toLocaleString()}`,
                fixed: true
            });

            const seenMacs = new Set();
            (data.results || []).forEach(device => {
                if (seenMacs.has(device.mac)) return;
                seenMacs.add(device.mac);

                const group = getIconGroup(device);
                const label = device.hostname ? `${device.hostname}\n${device.ip}` : `${device.ip}\n${device.vendor || 'Unknown'}`;

                const titleLines = [
                    `IP: ${device.ip}`,
                    `MAC: ${device.mac}`,
                    `Vendor: ${device.vendor || 'Unknown'}`
                ];
                if (device.hostname) titleLines.push(`Hostname: ${device.hostname}`);
                titleLines.push(`Type: ${device.type}`);
                if (device.rssi) titleLines.push(`RSSI: ${device.rssi} dBm`);
                if (device.ipv6) titleLines.push(`IPv6: ${device.ipv6}`);
                if (device.ping_rtt) titleLines.push(`Ping RTT: ${device.ping_rtt.toFixed(2)} ms`);
                if (device.rate) titleLines.push(`Rate Index: ${device.rate}`);
                if (device.is_ap) titleLines.push(`AP SSID: ${device.ap_ssid}`);
                if (device.snmp && device.snmp !== "-") titleLines.push(`SNMP: ${device.snmp}`);
                if (device.ports && device.ports !== "-") titleLines.push(`Ports: ${device.ports}`);
                if (device.mdns) titleLines.push(`mDNS: ${device.mdns}`);

                nodes.push({
                    id: device.mac,
                    label,
                    title: titleLines.join('\n'),
                    group
                });

                edges.push({
                    from: 'ESP32',
                    to: device.mac,
                    dashes: device.type === 'WIFI' || device.type === 'GATEWAY',
                    color: { color: '#848484' }
                });
            });

            const visData = {
                nodes: new window.vis.DataSet(nodes),
                edges: new window.vis.DataSet(edges)
            };

            const options = {
                autoResize: true,
                nodes: {
                    shape: 'dot',
                    size: 20,
                    font: { size: 14, color: '#333' },
                    borderWidth: 2
                },
                groups: {
                    esp32: { shape: 'icon', icon: { face: "'Font Awesome 6 Free'", code: '\uf2db', size: 50, color: '#FFD700', weight: 'bold' } },
                    gateway: { shape: 'icon', icon: { face: "'Font Awesome 6 Free'", code: '\uf0e8', size: 40, color: 'purple', weight: 'bold' } },
                    ap: { shape: 'icon', icon: { face: "'Font Awesome 6 Free'", code: '\uf1eb', size: 40, color: 'orange', weight: 'bold' } },
                    mobile: { shape: 'icon', icon: { face: "'Font Awesome 6 Free'", code: '\uf3ce', size: 30, color: 'green', weight: 'bold' } },
                    rpi: { shape: 'icon', icon: { face: "'Font Awesome 6 Free'", code: '\uf233', size: 30, color: '#C51A4A', weight: 'bold' } },
                    chip: { shape: 'icon', icon: { face: "'Font Awesome 6 Free'", code: '\uf2db', size: 30, color: 'gray', weight: 'bold' } },
                    camera: { shape: 'icon', icon: { face: "'Font Awesome 6 Free'", code: '\uf03d', size: 30, color: '#1E90FF', weight: 'bold' } },
                    printer: { shape: 'icon', icon: { face: "'Font Awesome 6 Free'", code: '\uf02f', size: 30, color: '#333', weight: 'bold' } },
                    desktop: { shape: 'icon', icon: { face: "'Font Awesome 6 Free'", code: '\uf108', size: 30, color: '#555', weight: 'bold' } }
                },
                interaction: {
                    hover: true,
                    tooltipDelay: 200
                },
                physics: {
                    enabled: true,
                    stabilization: {
                        iterations: 100
                    },
                    barnesHut: {
                        gravitationalConstant: -8000,
                        springConstant: 0.04,
                        springLength: 150
                    }
                }
            };

            network = new window.vis.Network(containerRef.current, visData, options);
            networkRef.current = network;
        }, waitTime);

        return () => {
            clearTimeout(timer);
            if (network) {
                network.destroy();
                networkRef.current = null;
            }
        };
    }, [data, height, isModal]);

    return <div ref={containerRef} className="network-diagram-container" style={{ height }}></div>;
};

const NetworkDiagram = ({ selectedDevice, onDeviceChange, selectedInterval, customRange, onIntervalChange, onRangeChange }) => {
    const [selectedScan, setSelectedScan] = useState(null);
    const { discoveryData: scans, loading, error } = useDiscoveryData(selectedDevice, selectedInterval, customRange);

    const openScan = (scan) => {
        setSelectedScan(scan);
    };

    const closeScan = () => {
        setSelectedScan(null);
    };

    return (
        <div className="dashboard-content network-diagram-page">
            <header style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                        <h2>Interactive Network Map</h2>
                        <div className="network-diagram-legend">
                            <span><i className="fas fa-sitemap" style={{ color: 'purple' }}></i> Gateway</span>
                            <span><i className="fas fa-wifi" style={{ color: 'orange' }}></i> AP</span>
                            <span><i className="fas fa-microchip" style={{ color: '#FFD700' }}></i> Scanner</span>
                            <span><i className="fas fa-mobile-alt" style={{ color: 'green' }}></i> Mobile</span>
                            <span><i className="fas fa-desktop" style={{ color: '#555' }}></i> LAN</span>
                        </div>
                    </div>
                </div>
            </header>

            <section className="data-controls">
                <div className="data-controls__layout">
                    <div className="data-controls__device">
                        <div className="data-controls__title">Scanner Selection</div>
                        <DeviceDropdown selectedDevice={selectedDevice} onDeviceChange={onDeviceChange} />
                    </div>
                    <span className="data-controls__divider" aria-hidden="true" />
                    <div className="data-controls__interval">
                        <div className="data-controls__interval-label">Scan Period</div>
                        <IntervalButtons
                            selectedInterval={selectedInterval}
                            onIntervalChange={onIntervalChange}
                            customRange={customRange}
                            onCustomRangeChange={onRangeChange}
                        />
                    </div>
                </div>
            </section>

            {error && (
                <div style={{ marginTop: '20px' }}>
                    <ErrorMessage message={error} title="Failed to load network data" />
                </div>
            )}

            {!loading && !error && (!scans || scans.length === 0) && selectedDevice && (
                <div style={{ marginTop: '30px' }}>
                    <LoadingMessage message="No discovery results found for the selected period." />
                </div>
            )}

            {loading && (
                <div style={{ marginTop: '30px' }}>
                    <LoadingMessage message={selectedDevice ? 'Loading network scans…' : 'Select a scanner to view network map.'} />
                </div>
            )}

            {!loading && !error && scans && scans.length > 0 && (
                <div className="scans-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', gap: '30px', marginTop: '30px' }}>
                    {scans.map((scan, index) => (
                        <div
                            key={scan.id || index}
                            style={{ position: 'relative', cursor: 'pointer' }}
                            onClick={() => openScan(scan)}
                        >
                            <div className="network-preview-card" style={{ background: 'rgba(255,255,255,0.02)', padding: '15px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)' }}>
                                <NetworkMap data={scan} height="300px" isModal={false} />
                                <div className="network-diagram-overlay-hint">
                                    <i className="fas fa-search-plus"></i> Click to Expand
                                </div>
                                <footer className="network-diagram-footer" style={{ marginTop: '10px' }}>
                                    <p style={{ fontWeight: 'bold' }}>Scan: {new Date(scan.scan_timestamp).toLocaleString()}</p>
                                    <p>{scan?.results?.length || 0} devices discovered</p>
                                </footer>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {!selectedDevice && !loading && (
                <div style={{ marginTop: '30px' }}>
                    <LoadingMessage message="Select a device to enable network visualization." />
                </div>
            )}

            {selectedScan && createPortal(
                <div className="network-diagram-modal">
                    <div className="network-diagram-modal-content">
                        <header className="network-diagram-modal-header">
                            <div>
                                <h3>Full Screen Network Map</h3>
                                <p style={{ margin: 0, fontSize: '0.9rem', color: '#64748b' }}>
                                    Scan timestamp: {new Date(selectedScan.scan_timestamp).toLocaleString()}
                                </p>
                            </div>
                            <button onClick={closeScan} className="refresh-button" style={{ background: '#f44336' }}>
                                <i className="fas fa-times"></i> Close
                            </button>
                        </header>
                        <div className="network-diagram-modal-body">
                            <NetworkMap data={selectedScan} height="calc(100vh - 120px)" isModal={true} />
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default NetworkDiagram;
